import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import WelcomeStep from './steps/WelcomeStep';
import ExperienceLevelStep from './steps/ExperienceLevelStep';
import CurrentHoldingsStep from './steps/CurrentHoldingsStep';
import RiskToleranceStep from './steps/RiskToleranceStep';
import InvestmentGoalsStep from './steps/InvestmentGoalsStep';
import PreferencesStep from './steps/PreferencesStep';
import FinalStep from './steps/FinalStep';

const ONBOARDING_STEPS = [
  { id: 'welcome', component: WelcomeStep, title: 'Welcome' },
  { id: 'experience', component: ExperienceLevelStep, title: 'Crypto Experience Level' },
  { id: 'holdings', component: CurrentHoldingsStep, title: 'Current Holdings' },
  { id: 'risk', component: RiskToleranceStep, title: 'Risk Tolerance' },
  { id: 'goals', component: InvestmentGoalsStep, title: 'Goal With Crypto' },
  { id: 'notifications', component: PreferencesStep, title: 'Notifications' },
  { id: 'final', component: FinalStep, title: 'Success Screen' }
];

export default function Onboarding() {
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { currentUser, userProfile, completeOnboarding } = useAuth();
  const mainContentRef = useRef(null);

  // Debug logging
  useEffect(() => {
    console.log('Onboarding Debug:', {
      currentUser: currentUser?.uid,
      userProfile: userProfile,
      onboardingCompleted: userProfile?.onboardingCompleted,
      currentStep
    });
  }, [currentUser, userProfile, currentStep]);

  // Check if user needs to complete onboarding
  useEffect(() => {
    if (currentUser && userProfile?.onboardingCompleted) {
      console.log('Onboarding already completed');
    }
  }, [currentUser, userProfile]);

  // Scroll to main content when step changes
  useEffect(() => {
    if (mainContentRef.current) {
      // Small delay to ensure the new step has rendered
      const timer = setTimeout(() => {
        try {
          // Check if smooth scrolling is supported
          if ('scrollBehavior' in document.documentElement.style) {
            mainContentRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            // Fallback for browsers without smooth scrolling support
            const elementTop = mainContentRef.current.offsetTop;
            const headerHeight = 80; // Approximate height of fixed header
            const scrollTop = Math.max(0, elementTop - headerHeight);
            
            window.scrollTo({
              top: scrollTop,
              behavior: 'auto'
            });
          }
        } catch (error) {
          // Fallback to simple scroll if there's an error
          console.warn('Smooth scrolling failed, using fallback:', error);
          mainContentRef.current.scrollIntoView();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep]);

  const handleNext = (stepData = {}) => {
    console.log('Moving to next step with data:', stepData);
    setOnboardingData(prev => ({ ...prev, ...stepData }));
    
    if (currentStep < ONBOARDING_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete(stepData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async (finalData = {}) => {
    try {
      setLoading(true);
      const completeData = { ...onboardingData, ...finalData };
      console.log('Completing onboarding with data:', completeData);
      await completeOnboarding(currentUser.uid, completeData);
      // The AppContent component will handle routing after onboarding completion
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setError('Failed to complete onboarding: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const currentStepComponent = ONBOARDING_STEPS[currentStep];
  const CurrentStepComponent = currentStepComponent.component;

  if (!currentUser) {
    return null; // Will redirect
  }

  if (userProfile?.onboardingCompleted) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 onboarding-step-transition">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-3">
            <div className="flex flex-col items-center text-center">
              <div className="text-sm font-medium text-gray-700 mb-1">
                Step {currentStep + 1} of {ONBOARDING_STEPS.length}
              </div>
              <div className="text-sm text-gray-500">
                {currentStepComponent.title}
              </div>
            </div>
            <div className="mt-2 mb-1">
              <div className="bg-gray-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${((currentStep + 1) / ONBOARDING_STEPS.length) * 100}%`
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pt-16 pb-8" ref={mainContentRef}>
        <div className="max-w-4xl mx-auto px-4">
          <CurrentStepComponent
            data={onboardingData}
            onNext={handleNext}
            onBack={handleBack}
            onComplete={handleComplete}
            loading={loading}
            currentStep={currentStep}
            totalSteps={ONBOARDING_STEPS.length}
            currentUser={currentUser}
          />
        </div>
      </div>
    </div>
  );
} 